import React, { createContext, useContext, useState, ReactNode } from 'react';

const STORAGE_KEY = 'linzara_view_mode';

type ViewMode = 'agnostic' | 'candidate' | 'employer';

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

function getInitialMode(): ViewMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'candidate' || stored === 'employer') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return 'agnostic';
}

export const ViewModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [viewMode, setViewModeState] = useState<ViewMode>(getInitialMode);

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    try {
      if (mode === 'agnostic') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, mode);
      }
    } catch {
      /* localStorage unavailable */
    }
  };

  return (
    <ViewModeContext.Provider value={{ viewMode, setViewMode }}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewMode must be used within ViewModeProvider');
  }
  return context;
};
